import { useState } from "react";
import { Lock, Eye, EyeOff, Loader2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { login, formatApiError, BRAND } from "@/lib/api";

export const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) {
      setError("Ingresa la contraseña");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const user = await login(password);
      onLogin(user);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail, "No se pudo iniciar sesión. Inténtalo de nuevo."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy relative overflow-hidden flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-[var(--green-500)] opacity-20 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-[var(--blue-600)] opacity-25 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="rounded-3xl bg-white shadow-2xl p-8 sm:p-10">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt={`${BRAND.name} logo`} className="h-14 w-14 rounded-2xl object-cover shadow-md ring-1 ring-slate-200" />
            <div>
              <p className="font-heading font-extrabold text-navy text-lg leading-tight">{BRAND.name}</p>
              <p className="text-[10px] tracking-[0.18em] font-semibold text-growth">PANEL DE LEADS</p>
            </div>
          </div>

          <h1 className="mt-8 text-2xl sm:text-3xl font-extrabold tracking-tight text-navy">Acceso privado</h1>
          <p className="mt-1 text-sm text-slate-600">Ingresa tu contraseña para ver los leads.</p>

          <form onSubmit={submit} noValidate className="mt-6 space-y-4" data-testid="admin-login-form">
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Contraseña"
                autoComplete="current-password"
                autoFocus
                className={`field !pr-12 ${error ? "field-error" : ""}`}
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-navy"
                aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
                data-testid="admin-password-toggle"
              >
                {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {error && (
              <p className="text-sm font-medium text-red-600" role="alert" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-cta w-full rounded-xl py-3.5 font-extrabold flex items-center justify-center gap-2 disabled:opacity-70"
              data-testid="admin-login-submit"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />}
              {loading ? "Verificando..." : "Entrar al panel"}
            </button>
          </form>

          <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-navy transition-colors" data-testid="admin-login-back-link">
            <ArrowLeft className="h-4 w-4" /> Volver a la landing
          </Link>
        </div>
      </div>
    </div>
  );
};
